import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import { isVideo } from './playlist';

// A video that makes no progress for this long counts as failed.
export const STALL_MS = 12000;

const AdvertisementMedia = ({ ad, url, active, turn, onEnded, onPlayed, onFailed }) => {
    const mediaRef = useRef(null);
    const stallTimer = useRef(null);
    const handlers = useRef({});
    handlers.current = { onEnded, onPlayed, onFailed };
    const video = isVideo(ad);

    const clearStall = () => clearTimeout(stallTimer.current);
    const armStall = () => {
        clearStall();
        stallTimer.current = setTimeout(() => handlers.current.onFailed(), STALL_MS);
    };

    useEffect(() => {
        const el = mediaRef.current;
        if (!el) return undefined;

        if (!active) {
            if (video) el.pause();
            return undefined;
        }
        // The file may have failed while it was loading in the background.
        if (!video) {
            if (el.complete && el.naturalWidth === 0) handlers.current.onFailed();
            return undefined;
        }
        if (el.error) {
            handlers.current.onFailed();
            return undefined;
        }

        el.currentTime = 0;
        armStall();
        el.play().catch((error) => {
            if (error?.name !== 'AbortError') handlers.current.onFailed();
        });
        return clearStall;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, turn, video]);

    const className = clsx('advertisement-media', { active });

    if (video) {
        return (
            <video
                ref={mediaRef}
                src={url}
                className={className}
                muted
                playsInline
                preload="auto"
                onPlaying={() => handlers.current.onPlayed()}
                onTimeUpdate={() => active && armStall()}
                onEnded={() => {
                    if (!active) return;
                    clearStall();
                    handlers.current.onEnded();
                }}
                onError={() => active && handlers.current.onFailed()}
            />
        );
    }

    return (
        <img
            ref={mediaRef}
            src={url}
            alt="Advertisement"
            className={className}
            onLoad={() => handlers.current.onPlayed()}
            onError={() => active && handlers.current.onFailed()}
        />
    );
};

export default AdvertisementMedia;
